// AUTO-GENERATED FILE - DO NOT EDIT
// Generated from config/resources/urls.yaml
// Regenerate with: task gen:config
// Generated at: 2026-08-19 01:26:49 UTC

/** All valid environment names. */
export const ENVIRONMENTS = [
  'local',
  'staging',
  'prod',
] as const;
export type Environment = (typeof ENVIRONMENTS)[number];

/** Service URLs for a given environment. */
export interface ServiceUrls {
  readonly api: string;
  readonly web: string;
  readonly dashboard: string;
  readonly docs: string;
  readonly echo: string;
  readonly engineering: string;
  readonly status: string;
}

/** URL mappings for all environments. */
export const URLS: Record<Environment, ServiceUrls> = {
  local: {
    api: 'http://localhost:3000',
    web: 'http://localhost:5174',
    dashboard: 'https://localhost:5173',
    docs: 'http://localhost:5175',
    echo: 'http://localhost:3000',
    engineering: 'http://localhost:5179',
    status: 'http://localhost:5180',
  },
  staging: {
    api: 'https://api.staging.pgns.dev',
    web: 'https://staging.pgns.dev',
    dashboard: 'https://app.staging.pgns.dev',
    docs: 'https://docs.staging.pgns.dev',
    echo: 'https://echo.staging.pgns.dev',
    engineering: 'https://engineering.staging.pgns.dev',
    status: 'https://status.staging.pgns.dev',
  },
  prod: {
    api: 'https://api.pgns.io',
    web: 'https://pgns.io',
    dashboard: 'https://app.pgns.io',
    docs: 'https://docs.pgns.io',
    echo: 'https://echo.pgns.io',
    engineering: 'https://engineering.pgns.io',
    status: 'https://status.pgns.io',
  },
};

/** Social / community links. */
export const SOCIALS = {
  github: 'https://github.com/pgns-io',
  discord: 'https://discord.gg/Wh3YeFAt',
  cli: 'https://github.com/pgns-io/cli',
  sdk_js: 'https://github.com/pgns-io/sdk-js',
  sdk_python: 'https://github.com/pgns-io/sdk-python',
  sdk_go: 'https://github.com/pgns-io/sdk-go',
  sdk_js_registry: 'https://www.npmjs.com/package/@pgns/sdk',
  sdk_python_registry: 'https://pypi.org/project/pgns/',
  sdk_go_registry: 'https://pkg.go.dev/github.com/pgns-io/sdk-go',
} as const;

/** CORS origins for all environments. */
export const CORS_ORIGINS: Record<Environment, readonly string[]> = {
  local: [
    'https://localhost:5173',
    'http://localhost:5173',
    'http://localhost:5174',
  ],
  staging: [
    'https://staging.pgns.dev',
    'https://app.staging.pgns.dev',
  ],
  prod: [
    'https://pgns.io',
    'https://app.pgns.io',
  ],
};

/**
 * Shift the port in a `host:port` URL by `offset`, leaving everything else
 * alone. Only meaningful for the `local` environment.
 */
function shiftPort(url: string, offset: number): string {
  return url.replace(/:(\d+)(?=$|\/|\?)/, (_match, port: string) => `:${Number(port) + offset}`);
}

/**
 * Get service URLs for an environment.
 *
 * `localPortOffset` shifts every local port so several git worktrees can run
 * their dev servers side by side (`pgns-wt` writes the offset into each
 * worktree's `.env.local`). Ignored outside the `local` environment.
 */
export function getUrls(env: Environment, localPortOffset = 0): ServiceUrls {
  const urls = URLS[env];
  if (env !== 'local' || !localPortOffset) return urls;
  return Object.fromEntries(
    Object.entries(urls).map(([key, value]) => [key, shiftPort(value, localPortOffset)]),
  ) as unknown as ServiceUrls;
}

/**
 * Get CORS origins for an environment.
 *
 * Deliberately has no port-offset variant: the server's `api_cors_layer`
 * already allows any `localhost`/`127.0.0.1` origin on any port for local dev,
 * so shifting these would be dead code.
 */
export function getCorsOrigins(env: Environment): readonly string[] {
  return CORS_ORIGINS[env];
}
